import React, { useState } from 'react';
import { IconHeart, IconMessageCircle, IconRepeat, IconEye } from '@tabler/icons-react';
import { Skeleton } from '../../../ui/skeleton';
import { useTheme } from '../../../../contexts/theme/ThemeContext';
import { format, parseISO } from 'date-fns';
import TwitterIframe from './TwitterIframe';

/**
 * Twitter Post Card Component
 * Clean design with visible metrics
 */
const TwitterPostCard = ({
  post,
  loading = false,
  index = 0
}) => {
  const { isDarkMode } = useTheme();
  const [imageLoading, setImageLoading] = useState(true);

  // Loading skeleton
  if (loading) {
    return (
      <div className="glass-card mb-4 border border-glass-border overflow-hidden">
        <div className="p-4">
          <Skeleton className="w-[200px] h-5 mb-2" />
          <Skeleton className="w-full h-[300px] mb-2" />
          <div className="flex gap-4">
            <Skeleton className="w-[60px] h-5" />
            <Skeleton className="w-[60px] h-5" />
            <Skeleton className="w-[60px] h-5" />
          </div>
        </div>
      </div>
    );
  }

  // Format date compactly
  const formatDate = (date) => {
    if (!date) return '';
    try {
      return format(parseISO(date), 'MMM d');
    } catch {
      return '';
    }
  };

  // Format numbers compactly
  const formatNumber = (num) => {
    if (!num && num !== 0) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  return (
    <div className="glass-card mb-4 border border-glass-border overflow-hidden max-w-[500px] mx-auto">
      {/* Compact header with better visibility */}
      <div className="px-4 py-2 border-b border-glass-border/50 bg-surface-1/30">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-text-primary">
            {post.creatorName || post.twitterHandle || 'Creator'}
          </span>

          {post.campaignName && (
            <>
              <span className="text-sm text-text-secondary">&bull;</span>
              <span className="text-sm text-text-secondary">
                {post.campaignName}
              </span>
            </>
          )}

          {post.submittedAt && (
            <>
              <span className="text-sm text-text-secondary">&bull;</span>
              <span className="text-sm text-text-secondary">
                {formatDate(post.submittedAt)}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Tweet content - main focus */}
      <div>
        <TwitterIframe
          tweetUrl={post.tweetUrl}
          campaignName={post.campaignName}
          onLoad={() => setImageLoading(false)}
        />
      </div>

      {/* Visible metrics footer with icons */}
      {post.metrics && Object.keys(post.metrics).length > 0 && (
        <div className="px-4 py-3 border-t border-glass-border/50 bg-surface-1/20">
          <div className="flex gap-5 items-center justify-center flex-wrap">
            {post.metrics.impressions !== undefined && (
              <div className="flex items-center gap-1">
                <IconEye size={18} className="text-text-secondary" />
                <span className="text-sm font-semibold text-text-primary">
                  {formatNumber(post.metrics.impressions)}
                </span>
              </div>
            )}

            {post.metrics.likes !== undefined && (
              <div className="flex items-center gap-1">
                <IconHeart size={18} className="text-gray-400" />
                <span className="text-sm font-semibold text-text-primary">
                  {formatNumber(post.metrics.likes)}
                </span>
              </div>
            )}

            {post.metrics.retweets !== undefined && (
              <div className="flex items-center gap-1">
                <IconRepeat size={18} className="text-accent" />
                <span className="text-sm font-semibold text-text-primary">
                  {formatNumber(post.metrics.retweets)}
                </span>
              </div>
            )}

            {post.metrics.replies !== undefined && (
              <div className="flex items-center gap-1">
                <IconMessageCircle size={18} className="text-text-secondary" />
                <span className="text-sm font-semibold text-text-primary">
                  {formatNumber(post.metrics.replies)}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TwitterPostCard;
